import {
  hasOutSourcingAnalyticsAccess,
  isOutSourcingUser,
  isSalesAdminUser,
  normalizeRole,
} from '../utils/permissions';
import { hasEmailAccess } from '../utils/emailAccessConfig';

/**
 * Telephony admin = the plain ADMIN role only. Everyone else (counsellors,
 * demo-ops, marketing, …) gets the Dialer and their own Logs; managing
 * agents and the number pool stays with admins.
 *
 * NOTE: this gates UI only — the platform API can't distinguish callers
 * until session federation lands, so treat it as tidiness, not security.
 */
export function isTelephonyAdmin(): boolean {
  try {
    const raw = localStorage.getItem('userProfileData');
    if (!raw) return false;
    const profile = JSON.parse(raw);
    if (normalizeRole(profile?.role) === 'ROLE_ADMIN') return true;
    const authorities = profile?.authorities;
    return (
      Array.isArray(authorities) &&
      authorities.some((a: string) => normalizeRole(a) === 'ROLE_ADMIN')
    );
  } catch {
    return false;
  }
}

/**
 * Agents/Numbers pages — restricted via EMAIL_ACCESS.telephonyManager
 * in utils/emailAccessConfig.ts (same pattern as Transactions/Rnr/TeacherChange).
 * Front-end-only; same caveat as isTelephonyAdmin above.
 */
export function isTelephonyManager(): boolean {
  return hasEmailAccess('telephonyManager');
}

/**
 * Who the Analytics team/agent pickers are populated for:
 * - 'outSourcing' — ROLE_OUT_SOURCING on the "outSourcingAnalytics" email list: only the
 *                   outsourcing team's agents, no team picker
 * - 'all'         — telephony admins (ROLE_ADMIN): every team/agent
 * - 'sales'       — ROLE_SALES_ADMIN (without ROLE_ADMIN): only the Sales and Sales & Renewal teams
 * - 'self'        — everyone else: their own calls, no pickers (the sidebar and
 *                   PrivateRoute keep non-admins off Analytics anyway)
 * Outsourcing is checked first so it wins over any admin role the same user also holds.
 */
export type TelephonyAnalyticsScope = 'all' | 'sales' | 'outSourcing' | 'self';

export function getTelephonyAnalyticsScope(): TelephonyAnalyticsScope {
  if (isOutSourcingUser()) return hasOutSourcingAnalyticsAccess() ? 'outSourcing' : 'self';
  if (isTelephonyAdmin()) return 'all';
  return isSalesAdminUser() ? 'sales' : 'self';
}

/** Telephony team holding the outsourced agents — matched on its key or name, e.g. "outsourcing" / "Out Sourcing". */
export const isOutSourcingTeam = (team?: { key?: string; name?: string } | null): boolean =>
  [team?.key, team?.name].some((value) =>
    String(value || '').toLowerCase().replace(/[^a-z]/g, '').includes('outsourc'),
  );

/** The two teams a SALES_ADMIN may read: "Sales" and "Sales & Renewal(s)", matched on the team name. */
const SALES_TEAM_NAMES = ['sales', 'salesrenewal', 'salesrenewals', 'salesandrenewal', 'salesandrenewals'];

export const isSalesTeam = (team?: { key?: string; name?: string } | null): boolean =>
  SALES_TEAM_NAMES.includes(String(team?.name || '').toLowerCase().replace(/[^a-z]/g, ''));
